import React from 'react';
import * as Styles from './styles';

interface PaginationBarProps {
  totalItems: number;
  totalPages: number;
  itemsPerPage: number;
  currentPage: number;
  width?: string;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number) => void;
}

const PaginationBar: React.FC<PaginationBarProps> = ({
  totalItems,
  totalPages,
  itemsPerPage,
  currentPage,
  onPageChange,
  onItemsPerPageChange,
  width,
}) => {
  const itemsPerPageOptions = [10, 20, 50];

  const handlePageClick = (page: number) => {
    if (page > 0 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const renderPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }

    return pages.map((page, index) =>
      typeof page === 'number' ? (
        <Styles.PageButton
          key={index}
          active={currentPage === page}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </Styles.PageButton>
      ) : (
        <Styles.Ellipsis key={index}>{page}</Styles.Ellipsis>
      ),
    );
  };

  return (
    <Styles.PaginationContainer width={width ?? '100%'}>
      {/* Left Side - Showing Items Count */}
      <Styles.ItemCount>
        Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}-
        {Math.min(currentPage * itemsPerPage, totalItems)} out of {totalItems}
      </Styles.ItemCount>

      {/* Center - Pagination */}
      <Styles.PageControls>
        <Styles.PageButton
          disabled={currentPage === 1}
          onClick={() => handlePageClick(currentPage - 1)}
        >
          {'<'}
        </Styles.PageButton>
        {renderPageNumbers()}
        <Styles.PageButton
          disabled={currentPage === totalPages}
          onClick={() => handlePageClick(currentPage + 1)}
        >
          {'>'}
        </Styles.PageButton>
      </Styles.PageControls>

      {/* Right Side - Items Per Page Dropdown */}
      <Styles.ItemsPerPage>
        <label>Show:</label>
        <Styles.Select
          value={itemsPerPage}
          onChange={e => onItemsPerPageChange(Number(e.target.value))}
        >
          {itemsPerPageOptions.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Styles.Select>
      </Styles.ItemsPerPage>
    </Styles.PaginationContainer>
  );
};

export default PaginationBar;
